import mapboxgl from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import defaultStyles from "fond/draw/defaultStyles";
import { MapboxStyleLayer } from "fond/types";

const isValid = `user_${DRAWING_CONSTANTS.Valid}`;
const isIntersect = `user_${DRAWING_CONSTANTS.Intersect}`;

/**
 * The MapboxDraw theme extended to include styles specific for planner projects
 * when drawing underground features.
 */
const modifiedDefaults: MapboxStyleLayer[] = defaultStyles.map((style) => {
  if (style.id === "gl-draw-point-stroke-active") {
    return {
      ...style,
      // We filter out the ug_points
      filter: [...(style.filter || []), ["!=", "user_layerId", "input_layers/ug_points"]],
    } as mapboxgl.CircleLayer;
  }

  // Override the line styles to include hover effects
  if (style.id === "gl-draw-line-inactive") {
    return {
      ...style,
      paint: {
        ...style.paint,
        "line-color": ["case", ["==", ["get", "user_meta:hover"], "true"], "#1489EF", ["case", ["==", ["get", isValid], "false"], "red", "#ffb265"]],
        "line-width": ["case", ["==", ["get", isValid], "false"], 4, 2],
        "line-dasharray": ["case", ["==", ["get", isValid], "false"], ["literal", [2, 2]], ["literal", [1]]],
      },
    } as mapboxgl.LineLayer;
  }

  if (style.id === "gl-draw-line-active") {
    return {
      ...style,
      paint: {
        ...style.paint,
        "line-dasharray": [1],
        "line-width": 3,
        "line-color": "#FF5832",
      },
    } as mapboxgl.LineLayer;
  }

  if (["gl-draw-point-active", "gl-draw-point-inactive"].includes(style.id)) {
    return {
      ...style,
      // We filter out the ug_points
      filter: [...(style.filter || []), ["!=", "user_layerId", "input_layers/ug_points"]],
    } as mapboxgl.SymbolLayer;
  }

  // Override the color of the vertex & mid point colors
  if (
    [
      // "gl-draw-point-active",
      "gl-draw-polygon-midpoint",
      "gl-draw-polygon-and-line-vertex-inactive",
      "gl-draw-polygon-and-line-vertex-stroke-inactive",
    ].includes(style.id)
  ) {
    return {
      ...style,
      paint: {
        ...style.paint,
        "circle-color": "#FF5832",
      },
    } as mapboxgl.CircleLayer;
  }

  return style;
});

modifiedDefaults.push({
  id: "gl-draw-point-ug_point-stroke-active",
  type: "circle",
  filter: [
    "all",
    ["==", "active", "true"],
    ["==", "$type", "Point"],
    ["==", "meta", "feature"],
    ["!=", "meta", "midpoint"],
    ["==", "user_layerId", "input_layers/ug_points"],
  ],
  paint: {
    "circle-radius": 10,
    "circle-color": "#FFF000",
  },
} as mapboxgl.CircleLayer);

modifiedDefaults.push(
  ...["active", "inactive"].map(
    (key) =>
      ({
        id: `gl-draw-point-ug_point-${key}`,
        type: "symbol",
        // We filter out the ug_points so that
        filter: [
          "all",
          ["==", "active", (key === "active").toString()],
          ["==", "$type", "Point"],
          ["==", "meta", "feature"],
          ["!=", "mode", "static"],
          ["==", "user_layerId", "input_layers/ug_points"],
        ],
        layout: {
          "text-field": "\uF096",
          "text-anchor": "bottom",
          "text-justify": "center",
          "text-allow-overlap": true,
          "text-ignore-placement": true,
          "text-font": ["fontello Regular"],
          "text-offset": [0.05, 0],
          "text-padding": 0,
          "text-rotate": 0.01,
          "text-size": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 14],
          "text-line-height": -0.45,
        },
        paint: {
          "text-opacity": 1,
          "text-translate": [-0.6, 0.1],
          "text-color": ["case", ["==", ["get", isValid], "false"], "red", "black"],
          "text-halo-color": ["case", ["==", ["get", isValid], "false"], ["case", ["==", ["get", isIntersect], "true"], "green", "red"], "black"],
          "text-halo-width": ["case", ["==", ["get", isValid], "false"], 2, 0],
        },
      }) as mapboxgl.SymbolLayer
  )
);

export default modifiedDefaults;
